<script setup lang="ts">
import DSContainer from '~/components/ds/Container.vue'
import { useBlog, type BlogArticleListItem, type BlogCategoryItem, type BlogTagItem } from '~/composables/useBlog'

const { t, locale } = useI18n()
const route = useRoute()
const localePath = useLocalePath()
const { fetchArticles, fetchCategories, fetchTags } = useBlog()

const page = computed(() => Math.max(0, Number(route.query.page || 0)))
const pageSize = 12

const articlesResult = await useAsyncData(
  () => `blog-list:${locale.value}:${page.value}`,
  () => fetchArticles({ page: page.value, size: pageSize }),
  { watch: [page, locale] }
)
const articles = computed<BlogArticleListItem[]>(() => articlesResult.data.value?.items ?? [])
const totalPages = computed<number>(() => articlesResult.data.value?.totalPages ?? 0)
const total = computed<number>(() => articlesResult.data.value?.total ?? 0)

const { data: categories } = await useAsyncData<BlogCategoryItem[]>(() => `blog-categories:${locale.value}`, fetchCategories, { watch: [locale] })
const { data: tags } = await useAsyncData<BlogTagItem[]>(() => `blog-tags:${locale.value}`, fetchTags, { watch: [locale] })

const { canonicalUrl: canonical } = useSeoCanonical('/blog')

// A locale with no published articles renders an empty listing that Google
// flags as a soft 404 (e.g. /en/blog). Mark the empty shell noindex; once
// articles exist for that locale it becomes indexable again.
useSeoMeta({
  robots: () => (total.value === 0 ? 'noindex, follow' : 'index, follow'),
})

useHead({
  title: () => t('blog.pageTitle'),
  meta: [
    { name: 'description', content: () => t('blog.pageDescription') },
    { property: 'og:type', content: 'website' },
    { property: 'og:title', content: () => t('blog.pageTitle') },
    { property: 'og:description', content: () => t('blog.pageDescription') },
    { property: 'og:url', content: () => canonical.value },
  ],
})

function formatDate(d?: string) {
  if (!d) return ''
  return new Date(d).toLocaleDateString()
}
</script>

<template>
  <DSContainer class="py-8">
    <header class="mb-10">
      <h1 class="text-4xl font-bold tracking-tight">{{ t('blog.pageTitle') }}</h1>
      <p class="mt-3 text-lg text-gray-600 dark:text-gray-400">{{ t('blog.pageDescription') }}</p>
    </header>

    <div class="grid grid-cols-1 lg:grid-cols-4 gap-8">
      <main class="lg:col-span-3 space-y-8">
        <article
          v-for="(article, idx) in articles"
          :key="article.id"
          class="group flex flex-col md:flex-row gap-6 pb-8 border-b border-gray-200 dark:border-gray-800 last:border-0"
        >
          <NuxtLink
            :to="localePath(`/blog/${article.slug}`)"
            class="md:w-64 flex-shrink-0 aspect-video md:aspect-[4/3] rounded-lg overflow-hidden bg-gray-100 dark:bg-gray-800 block"
          >
            <NuxtImg
              v-if="article.coverImageUrl"
              :src="article.coverImageUrl"
              :alt="article.title"
              class="w-full h-full object-cover group-hover:scale-105 transition-transform"
              width="400"
              height="300"
              :loading="idx === 0 ? 'eager' : 'lazy'"
              :fetchpriority="idx === 0 ? 'high' : 'auto'"
            />
          </NuxtLink>
          <div class="flex-1 min-w-0">
            <div v-if="article.category" class="text-xs font-medium uppercase tracking-wide text-primary-600 dark:text-primary-400">
              <NuxtLink :to="localePath(`/blog/category/${article.category.slug}`)" class="hover:underline">
                {{ article.category.name }}
              </NuxtLink>
            </div>
            <h2 class="mt-2 text-2xl font-bold leading-tight">
              <NuxtLink :to="localePath(`/blog/${article.slug}`)" class="hover:text-primary-600 dark:hover:text-primary-400">
                {{ article.title }}
              </NuxtLink>
            </h2>
            <p v-if="article.excerpt" class="mt-3 text-gray-600 dark:text-gray-400 line-clamp-3">{{ article.excerpt }}</p>
            <div class="mt-4 flex items-center gap-3 text-sm text-gray-500">
              <span>{{ article.authorName }}</span>
              <span>·</span>
              <time>{{ formatDate(article.publishedAt) }}</time>
              <span v-if="article.readingMinutes">·</span>
              <span v-if="article.readingMinutes">{{ t('blog.minutesRead', { n: article.readingMinutes }) }}</span>
            </div>
          </div>
        </article>

        <div v-if="articles.length === 0" class="py-16 text-center text-gray-500">
          {{ t('blog.empty') }}
        </div>

        <nav v-if="totalPages > 1" class="flex justify-center gap-2 pt-6" aria-label="pagination">
          <NuxtLink
            v-if="page > 0"
            :to="{ path: localePath('/blog'), query: { page: page - 1 } }"
            class="px-4 py-2 border rounded-md hover:bg-gray-50 dark:hover:bg-gray-800"
          >
            {{ t('blog.previous') }}
          </NuxtLink>
          <span class="px-4 py-2 text-sm text-gray-500">{{ page + 1 }} / {{ totalPages }}</span>
          <NuxtLink
            v-if="page < totalPages - 1"
            :to="{ path: localePath('/blog'), query: { page: page + 1 } }"
            class="px-4 py-2 border rounded-md hover:bg-gray-50 dark:hover:bg-gray-800"
          >
            {{ t('blog.next') }}
          </NuxtLink>
        </nav>
      </main>

      <aside class="space-y-8">
        <div v-if="categories?.length">
          <h3 class="text-sm font-semibold uppercase tracking-wide text-gray-500 mb-3">{{ t('blog.categoriesTitle') }}</h3>
          <ul class="space-y-2">
            <li v-for="c in categories" :key="c.slug">
              <NuxtLink
                :to="localePath(`/blog/category/${c.slug}`)"
                class="flex items-center justify-between text-sm hover:text-primary-600 dark:hover:text-primary-400"
              >
                <span>{{ c.name }}</span>
                <span class="text-gray-400">{{ c.articleCount }}</span>
              </NuxtLink>
            </li>
          </ul>
        </div>

        <div v-if="tags?.length">
          <h3 class="text-sm font-semibold uppercase tracking-wide text-gray-500 mb-3">{{ t('blog.tagsTitle') }}</h3>
          <div class="flex flex-wrap gap-2">
            <NuxtLink
              v-for="t in tags"
              :key="t.slug"
              :to="localePath(`/blog/tag/${t.slug}`)"
              class="text-xs px-2.5 py-1 rounded-full bg-gray-100 dark:bg-gray-800 hover:bg-primary-100 dark:hover:bg-primary-900/30"
            >
              #{{ t.name }}
              <span class="ml-1 text-gray-400">{{ t.articleCount }}</span>
            </NuxtLink>
          </div>
        </div>
      </aside>
    </div>
  </DSContainer>
</template>
